import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import DateRangeFilter from '@/components/DateRangeFilter';
import { Box, Title } from '@/components/Dropdown';
import Text from '@/components/Text';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import { api } from '@/utils/api';
import {
  debtorBanks,
  invoiceSourceLabels,
  invoiceSources,
  paymentMethodLabels,
  paymentMethods,
} from '@/utils/data/payments';

import IdFilterDropdown from './IdFilterDropdown';
import InvoicesFilterBooleanDropdown from './InvoicesFilterBooleanDropdown';
import InvoicesFilterDropdown from './InvoicesFilterDropdown';

const StyledBox = styled(Box)`
  width: 100%;
  padding: 26px 24px 32px;
`;

const FilterHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const FilterFooter = styled.div`
  margin-top: 24px;
  text-align: right;
`;

const BodyHeaderLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  margin: 0 16px;
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.colors.errorLight};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    cursor: not-allowed;
    &:hover,
    &:active {
      border-color: transparent;
    }
  }
`;

const FilterGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 16px;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

const DateFilterTitle = styled(Title)`
  margin-bottom: 12px;
  padding: 0;
`;

interface Props {
  selectedBanks: string[];
  selectedDepartments: number[];
  selectedCompanies: number[];
  selectedPaymentTypes: string[];
  paidAtDateRange: { rangeStart?: DateTime; rangeEnd?: DateTime };
  setPaidAtDateRange: React.Dispatch<
    React.SetStateAction<{ rangeStart?: DateTime; rangeEnd?: DateTime }>
  >;
  selectedCreators: number[];
  selectedBankTypes: string[];
  selectedSources: string[];
  selectedBooleanValues: {
    gpm: boolean;
    thousand: boolean;
    missingBank: boolean;
    proforma: boolean;
  };
  setSelectedBanks: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedDepartments: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedCompanies: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedPaymentTypes: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedCreators: React.Dispatch<React.SetStateAction<number[]>>;
  setSelectedBankTypes: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedSources: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedBooleanValues: React.Dispatch<
    React.SetStateAction<{
      gpm: boolean;
      thousand: boolean;
      missingBank: boolean;
      proforma: boolean;
    }>
  >;
  closeFilter: () => void;
}

const InvoicesFilterBox: FC<Props> = ({
  selectedBanks,
  selectedDepartments,
  selectedCompanies,
  selectedPaymentTypes,
  selectedCreators,
  selectedBankTypes,
  selectedSources,
  selectedBooleanValues,
  paidAtDateRange,
  setSelectedBanks,
  setSelectedDepartments,
  setSelectedCompanies,
  setSelectedPaymentTypes,
  setSelectedCreators,
  setSelectedBankTypes,
  setSelectedSources,
  setSelectedBooleanValues,
  setPaidAtDateRange,
  closeFilter,
}) => {
  const [selectedLocalBanks, setSelectedLocalBanks] =
    useState<string[]>(selectedBanks);

  const [selectedLocalDepartments, setSelectedLocalDepartments] =
    useState<number[]>(selectedDepartments);

  const { data: departmentOptions = [] } = useDepartmentsOptions();

  const [selectedLocalCompanies, setSelectedLocalCompanies] =
    useState<number[]>(selectedCompanies);

  const [selectedLocalPaymentTypes, setSelectedLocalPaymentTypes] =
    useState<string[]>(selectedPaymentTypes);

  const [selectedLocalCreators, setSelectedLocalCreators] =
    useState<number[]>(selectedCreators);

  const [selectedLocalBankTypes, setSelectedLocalBankTypes] =
    useState<string[]>(selectedBankTypes);

  const [selectedLocalSources, setSelectedLocalSources] =
    useState<string[]>(selectedSources);

  const [localPaidAtDateRange, setLocalPaidAtDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>(paidAtDateRange);

  const [filterBooleanValues, setFilterBooleanValues] = useState<{
    gpm: boolean;
    thousand: boolean;
    missingBank: boolean;
    proforma: boolean;
  }>({
    gpm: selectedBooleanValues.gpm,
    thousand: selectedBooleanValues.thousand,
    missingBank: selectedBooleanValues.missingBank,
    proforma: selectedBooleanValues.proforma,
  });

  const { data } = useQuery(
    ['banks_and_departments'],
    () =>
      api.get<
        never,
        {
          banks: string[];
          departments: string[];
          payment_types: string[];
          countries: string[];
          companies: { id: number; name: string; short: string | null }[];
          users: {
            id: number;
            name: string;
          }[];
        }
      >(`/v1/payments/banks-and-departments`),
    {
      staleTime: 60 * 1000,
    },
  );

  const creators = data?.users.map((user) => {
    return { name: user.name, value: user.id };
  });

  const onFilterApply = () => {
    setSelectedBanks(selectedLocalBanks);
    setSelectedDepartments(selectedLocalDepartments);
    setSelectedCompanies(selectedLocalCompanies);
    setSelectedPaymentTypes(selectedLocalPaymentTypes);
    setSelectedCreators(selectedLocalCreators);
    setSelectedBankTypes(selectedLocalBankTypes);
    setSelectedSources(selectedLocalSources);
    setSelectedBooleanValues(filterBooleanValues);
    setPaidAtDateRange(localPaidAtDateRange);
    closeFilter();
  };

  const onFilterReset = () => {
    setSelectedBanks([]);
    setSelectedDepartments([]);
    setSelectedCompanies([]);
    setSelectedPaymentTypes([]);
    setSelectedCreators([]);
    setSelectedBankTypes([]);
    setSelectedSources([]);
    setSelectedBooleanValues({
      gpm: false,
      thousand: false,
      missingBank: false,
      proforma: false,
    });
    setPaidAtDateRange({});
    closeFilter();
  };

  return (
    <StyledBox>
      <FilterHeading>
        <Text $type="button">Filter</Text>
        <BodyHeaderLine />
        <ResetButton onClick={() => onFilterReset()}>
          <Text $color="error" $type="smallHeadline">
            Reset
          </Text>
        </ResetButton>
      </FilterHeading>
      <FilterGroup>
        <IdFilterDropdown
          filter={selectedLocalDepartments}
          setFilter={setSelectedLocalDepartments}
          options={departmentOptions.map((dept) => ({
            name: dept.label,
            value: dept.value,
          }))}
          title="By Department:"
        />
        <IdFilterDropdown
          filter={selectedLocalCompanies}
          setFilter={setSelectedLocalCompanies}
          options={data?.companies?.map((company) => ({
            name: company.name,
            value: company.id,
          }))}
          title="By Company:"
        />
        <InvoicesFilterDropdown
          filter={selectedLocalBanks}
          setFilter={setSelectedLocalBanks}
          options={debtorBanks.map((bank) => bank.value)}
          title="By Debtor Bank:"
        />
        <InvoicesFilterDropdown
          filter={selectedLocalPaymentTypes}
          setFilter={setSelectedLocalPaymentTypes}
          options={data?.payment_types.filter(
            (payment_type) => payment_type !== null,
          )}
          title="By Type:"
        />
        <IdFilterDropdown
          filter={selectedLocalCreators}
          setFilter={setSelectedLocalCreators}
          options={creators}
          title="By Creator:"
        />
        <InvoicesFilterDropdown
          filter={selectedLocalBankTypes}
          setFilter={setSelectedLocalBankTypes}
          options={paymentMethods.map((method) => paymentMethodLabels[method])}
          title="By Payment Type:"
        />
        <div>
          <InvoicesFilterBooleanDropdown
            label="With Gpm Only:"
            value={filterBooleanValues.gpm}
            setValue={setFilterBooleanValues}
            name="gpm"
          />
        </div>
        <div>
          <InvoicesFilterBooleanDropdown
            label="Only Over 1k:"
            value={filterBooleanValues.thousand}
            setValue={setFilterBooleanValues}
            name="thousand"
          />
        </div>
        <div>
          <InvoicesFilterBooleanDropdown
            label="Missing Bank Info Only:"
            value={filterBooleanValues.missingBank}
            setValue={setFilterBooleanValues}
            name="missingBank"
          />
        </div>
        <div>
          <InvoicesFilterBooleanDropdown
            label="Proforma Only:"
            value={filterBooleanValues.proforma}
            setValue={setFilterBooleanValues}
            name="proforma"
          />
        </div>
        <InvoicesFilterDropdown
          filter={selectedLocalSources}
          setFilter={setSelectedLocalSources}
          options={invoiceSources.map((source) => invoiceSourceLabels[source])}
          title="By Source:"
        />
        <div>
          <DateFilterTitle $color="black" $type="smallBold">
            By Paid Date:
          </DateFilterTitle>
          <DateRangeFilter
            selectedDateRange={localPaidAtDateRange}
            setSelectedDateRange={setLocalPaidAtDateRange}
            variant="select"
          />
        </div>
      </FilterGroup>
      <FilterFooter>
        <StyledButton onClick={closeFilter} color="secondary">
          Cancel
        </StyledButton>
        <ApplyButton color="colorized" onClick={onFilterApply}>
          Apply filter
        </ApplyButton>
      </FilterFooter>
    </StyledBox>
  );
};

export default InvoicesFilterBox;
